import { LoadingButton } from "@mui/lab";

import {
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Form, FormikProvider, useFormik } from "formik";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import {
  remoteOperationTaskUrl,
  remoteOperationUrl,
} from "src/react-query/endPoints";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { remoteOperationsDateFormat } from "src/utils/formatTime";
import { remoteOperationConfig } from "./remoteOperationConfig";
import { remoteOpsStatusSlice } from "src/store/remoteOpsStatus/remoteOpsStatus.slice";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { useAuth } from "src/hooks/useAuth.hook";
import { DateTimePicker } from "../../FormikDateTimePicker";

/** ✅ Normalize any operation name (handles underscores, casing, etc.) */
const normalizeOperation = (op) => {
  if (!op) return "";
  const formatted = op.replace(/[\s-_]/g, "").toLowerCase();
  const map = {
    changeavailability: "ChangeAvailability",
    changeconfiguration: "ChangeConfiguration",
    getconfiguration: "GetConfiguration",
    remotestart: "RemoteStart",
    remotestop: "RemoteStop",
    getdiagnostics: "GetDiagnostics",
    updatefirmware: "UpdateFirmware",
    reset: "Reset",
    triggermessage: "TriggerMessage",
    unlockconnector: "UnlockConnector",
    clearcache: "ClearCache",
  };
  return map[formatted] || op;
};

export const RemoteOperationForm = ({
  operation,
  initialValues,
  validationSchema,
  onClose,
  chargerId,
  chargerPk,
  chargerName,
  queryKeysDropdown,
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dateTimeErr, setDateTimeErr] = useState({ fieldName: "", message: "" });
  const { teamId } = useSelectedTeam();
  const {
    user: { email },
  } = useAuth();

  const normalizedOp = normalizeOperation(operation);



  const results = useQueries({
    queries:
      queryKeysDropdown?.map(({ queryKey: key, usePk, useEmail }) => ({
        queryKey: !useEmail
          ? queryKeys[key](teamId, usePk ? chargerPk : chargerId)
          : queryKeys[key](teamId, usePk ? chargerPk : chargerId, email),
        queryFn: fetchWithError,
        staleTime: 0,
        select: (data) => ({
          data: data?.data,
          key,
        }),
      })) ?? [],
  });

  const dropdownOptions = useMemo(
    () =>
      results?.reduce(
        (acc, curr) => ({
          ...acc,
          [curr?.data?.key]: curr?.data?.data,
        }),
        {}
      ) ?? {},
    [results]
  );

  const onRemoteOperationStatus = (data) => {
    // This function is no longer used for redirect, but kept for potential future use
    if (onClose) onClose();
    dispatch(
      remoteOpsStatusSlice.actions.setRemoteOps({
        open: true,
        operation,
        data: { chargerId, response: data.data.status },
      })
    );
    resetForm();
    queryKeysDropdown.forEach(({ queryKey: key, usePk }) =>
      queryClient.invalidateQueries(queryKeys[key](teamId, usePk ? chargerPk : chargerId))
    );
  };

  const { mutate, reset, isLoading } = useMutate("chargers", {}, false);

  const onOperationSent = (data) => {
    reset();
    resetForm();
    queryKeysDropdown.forEach(({ queryKey: key, usePk }) =>
      queryClient.invalidateQueries(queryKeys[key](teamId, usePk ? chargerPk : chargerId))
    );
    // Invalidate the main charger query so settings tab reflects any background updates
    if (chargerPk) {
      queryClient.invalidateQueries(queryKeys.charger(teamId, chargerPk));
    }
    
    // Redirect to logs tab immediately after successful remote operation API call
    if (chargerPk) {
      if (onClose) onClose();
      // Dispatch Redux action to set logs tab
      dispatch(
        tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
          detailsId: chargerId,
          tabOpenedFor: "charger",
          tabOpened: "logs",
        })
      );
      // Navigate to charger page - use replace to ensure it works even if already on the page
      navigate(`/charger/${chargerPk}`, { replace: false });
    }
  };

  const onSubmit = (data, { setSubmitting, setErrors }) => {
    let payload = { ...data };
    
    switch (normalizedOp) {
      case "GetDiagnostics":
        payload.start = remoteOperationsDateFormat(payload.start);
        payload.stop = remoteOperationsDateFormat(payload.stop);
        break;
      case "UpdateFirmware":
        payload.retrieveDate = remoteOperationsDateFormat(payload.retrieveDate);
        payload.stop = remoteOperationsDateFormat(payload.stop);
        break;
      case "ChangeConfiguration":
        let errors = { customConfKey: "", confKey: "" };
        if (payload.keyType === "Custom") {
          errors.customConfKey = !payload.customConfKey ? "This field is required" : "";
        } else {
          errors.confKey = !payload.confKey ? "This field is required" : "";
        }
        if (Object.values(errors).includes("This field is required")) {
          setErrors(errors);
          setSubmitting(false);
          return;
        }
        break;
      case "GetConfiguration":
        // Handle different keyType options
        if (payload.keyType === "All") {
          // Get all predefined configuration keys from the config
          const getConfigFields = remoteOperationConfig["GetConfiguration"] || [];
          const confKeysField = getConfigFields.find(field => field.fieldName === "confKeys");
          const allPredefinedKeys = (confKeysField?.options || []).map(opt => opt?.value ?? opt);
          // Send all predefined keys as comma-separated string
          payload.configKeyList = allPredefinedKeys.join(',');
        } else if (payload.keyType === "Select Keys") {
          // Convert selected predefined keys array to comma-separated string
          payload.configKeyList = (payload.confKeys || []).join(',');
        } else if (payload.keyType === "Custom") {
          // Use custom keys as comma-separated string (trim whitespace)
          if (payload.customKeys && payload.customKeys.trim()) {
            const customKeysArray = payload.customKeys.split(',').map(k => k.trim()).filter(k => k);
            payload.configKeyList = customKeysArray.join(',');
          } else {
            payload.configKeyList = "";
          }
        }
        // Remove the intermediate fields from payload
        delete payload.keyType;
        delete payload.confKeys;
        delete payload.customKeys;
        break;
      default:
        break;
    }

    //   parameters: payload,
    //   chargerId,
    //   remoteOperationType: normalizedOp,
    // });

    mutate(
      {
        url: remoteOperationUrl,
        method: "POST",
        body: {
          parameters: payload,
          chargerId,
          remoteOperationType: normalizedOp,
        },
      },
      { onSuccess: onOperationSent }
    );
    setSubmitting(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    values,
    resetForm,
  } = formik;

  const allowedDateTime = (currentFieldName, allFieldsArr) => {
    const correspondingFieldName = allFieldsArr.find(
      ({ fieldName, type }) => fieldName !== currentFieldName && type === "DATE/TIME"
    )?.fieldName;
    let allowed;
    switch (currentFieldName) {
      case "start":
      case "retrieveDate":
        allowed = new Date();
        allowed.setMinutes(allowed.getMinutes() - 5);
        break;
      case "stop":
        if (values[correspondingFieldName]) {
          allowed = new Date(values[correspondingFieldName]);
          allowed.setHours(allowed.getHours() + 1);
        } else allowed = new Date();
        break;
      default:
        break;
    }
    return allowed;
  };

  const hasFields = (remoteOperationConfig[normalizedOp] || []).length > 0;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" id="remoteOperations" noValidate onSubmit={handleSubmit}>
        <Card sx={{ p: 2, minHeight: 200 }}>
          <Grid container spacing={2}>
            {/* Show charger ID for operations with no fields */}
            {!hasFields && chargerId && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    border: "1px solid rgba(25, 118, 210, 0.2)",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    Chargebox ID:
                  </Typography>
                  <Typography variant="h6" sx={{ color: "primary.main", fontWeight: 600 }}>
                    {chargerId}
                  </Typography>
                </Box>
              </Grid>
            )}
            {(remoteOperationConfig[normalizedOp] || []).map(
              (
                {
                  fieldName,
                  required,
                  type,
                  label,
                  options,
                  queryKey,
                  formatOptions,
                  componentDependentOn,
                },
                index
              ) => {
                const allFieldsArr = remoteOperationConfig[normalizedOp] || [];

                // Check if field should be rendered based on componentDependentOn
                if (componentDependentOn) {
                  const { field, requiredCriteriaValue, disabledCriteriaValue } = componentDependentOn;
                  const dependentFieldValue = values[field];

                  // If the dependent field has a value that should disable this field, don't render it
                  if (disabledCriteriaValue && dependentFieldValue === disabledCriteriaValue) {
                    return null;
                  }

                  // If the dependent field doesn't have the required value, don't render it
                  if (requiredCriteriaValue && dependentFieldValue !== requiredCriteriaValue) {
                    return null;
                  }
                }

                switch (type) {
                  case "TEXT":
                    return (
                      <Grid key={`${fieldName}-${index}`} item xs={12} md={6}>
                        <FormControl fullWidth size="small">
                          <TextField
                            name={fieldName}
                            required={required}
                            label={label}
                            size="small"
                            {...getFieldProps(fieldName)}
                            error={Boolean(touched[fieldName] && errors[fieldName])}
                            helperText={touched[fieldName] && errors[fieldName]}
                          />
                        </FormControl>
                      </Grid>
                    );

                  case "DROPDOWN":
                    return (
                      <Grid key={`${fieldName}-${index}`} item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={Boolean(touched[fieldName] && errors[fieldName])}
                        >
                          <InputLabel id={fieldName}>{label}</InputLabel>
                          <Select
                            labelId={fieldName}
                            label={label}
                            name={fieldName}
                            value={values[fieldName] || ""}
                            onChange={(event) => setFieldValue(fieldName, event.target.value)}
                            required={required}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {options?.map((item, itemIndex) => (
                              <MenuItem
                                key={`${fieldName}-option-${itemIndex}`}
                                value={item?.value ?? item}
                              >
                                {item?.label ?? item}
                              </MenuItem>
                            ))}
                            {queryKey &&
                              dropdownOptions[queryKey]?.map((item, itemIndex) =>
                                formatOptions({ ...item, key: `${fieldName}-query-${itemIndex}` })
                              )}
                          </Select>
                          {touched[fieldName] && (
                            <FormHelperText>{errors[fieldName]}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    );

                  case "MULTISELECT":
                    return (
                      <Grid key={`${fieldName}-${index}`} item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          size="small"
                          error={Boolean(touched[fieldName] && errors[fieldName])}
                        >
                          <InputLabel id={fieldName}>{label}</InputLabel>
                          <Select
                            labelId={fieldName}
                            label={label}
                            name={fieldName}
                            multiple
                            value={values[fieldName] || []}
                            onChange={(event) => {
                              const value = event.target.value;
                              setFieldValue(fieldName, typeof value === 'string' ? value.split(',') : value);
                            }}
                            input={<OutlinedInput id={`${fieldName}-chip`} label={label} />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => {
                                  const option = options?.find(opt => (opt?.value ?? opt) === value);
                                  const displayLabel = option?.label ?? option ?? value;
                                  return (
                                    <Chip
                                      key={value}
                                      label={displayLabel}
                                      size="small"
                                      sx={{
                                        backgroundColor: "rgba(25, 118, 210, 0.1)",
                                        color: "primary.main",
                                        fontWeight: 500,
                                      }}
                                    />
                                  );
                                })}
                              </Box>
                            )}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 300,
                                  width: 250,
                                },
                              },
                            }}
                            required={required}
                          >
                            {options?.map((item, itemIndex) => {
                              const itemValue = item?.value ?? item;
                              const itemLabel = item?.label ?? item;
                              return (
                                <MenuItem
                                  key={`${fieldName}-option-${itemIndex}`}
                                  value={itemValue}
                                >
                                  {itemLabel}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {touched[fieldName] && (
                            <FormHelperText>{errors[fieldName]}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    );

                  case "DATE/TIME":
                    return (
                      <Grid item xs={12} md={6} key={`${fieldName}-${index}`}>
                        <DateTimePicker
                          size="small"
                          label={label}
                          mode="datetime"
                          value={values[fieldName] || null}
                          onChange={(newValue) => setFieldValue(fieldName, newValue)}
                          error={Boolean(
                            (touched[fieldName] && errors[fieldName]) ||
                              (touched[fieldName] &&
                                dateTimeErr.fieldName === fieldName &&
                                dateTimeErr.message)
                          )}
                          helperText={
                            (dateTimeErr.fieldName === fieldName && dateTimeErr.message
                              ? dateTimeErr.message
                              : "") ||
                            (touched[fieldName] && errors[fieldName])
                          }
                        />
                      </Grid>
                    );
                  default:
                    return null;
                }
              }
            )}
          </Grid>
        </Card>

        <Grid container mt={4} justifyContent="end">
          <Grid item xs={6} lg={6}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <LoadingButton
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting || isLoading}
                loadingIndicator="Sending..."
              >
                Send Message
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </Form>
    </FormikProvider>
  );
};